export class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this._onTopSearchClick = this._onTopSearchClick.bind(this);
    let debounceTimeout;
    this._cancelDebouncedInput = () => {
      clearTimeout(debounceTimeout);
    };
    this._onInput = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => this.onChange(), 700);
    };
    this._onFocus = () => this.onChange();
  }

  connectedCallback() {
    this.input = this.querySelector('input[name="q"]');
    // Queried by data attribute, not id: ids carry an instance suffix when more than one
    // predictive search is on the page (search drawer + mobile nav drawer).
    this.predictiveSearchResults = this.querySelector('[data-predictive-results]');
    this.topSearchesPanel = this.querySelector('[data-top-searches]');
    this.statusRegion = this.querySelector('[data-predictive-status]');
    if (!this.input || !this.predictiveSearchResults) {
      return;
    }

    this.searchTerm = this.input.value.trim();
    this.isOpen = false;

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('focus', this._onFocus);
    this.addEventListener('click', this._onTopSearchClick);
    document.addEventListener('click', this.handleClickOutside);
    this.close();
  }

  disconnectedCallback() {
    this._cancelDebouncedInput();
    this.removeEventListener('click', this._onTopSearchClick);
    if (this.input) {
      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('focus', this._onFocus);
    }
    document.removeEventListener('click', this.handleClickOutside);
  }

  _onTopSearchClick(event) {
    const btn = event.target.closest('[data-top-search-term]');
    if (!btn || !this.contains(btn)) return;
    event.preventDefault();
    const term = btn.getAttribute('data-top-search-term')?.trim() ?? '';
    this.applyTopSearchTerm(term);
  }

  /**
   * Fills the search field and loads predictive results in-place (no navigation).
   * @param {string} term
   */
  applyTopSearchTerm(term) {
    const t = term.trim();
    if (!this.input || !t) return;
    this._cancelDebouncedInput();
    this.input.value = t;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.searchTerm = t;
    this.topSearchesPanel?.style.setProperty('display', 'none');
    this.toggleLoading(true);
    this.input.focus();
    this.getSearchResults(t);
  }

  canShowTopSearches() {
    return Boolean(this.topSearchesPanel?.querySelector('[data-top-search-term]'));
  }

  onChange() {
    if (!this.input) return;

    const newSearchTerm = this.input.value.trim();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      const resultsElement = this.querySelector('.predictive-search-results');
      if (resultsElement) resultsElement.remove();
      if (this.canShowTopSearches()) {
        this.openTopSearches();
      } else {
        this.close();
      }
      return;
    }

    const topWasVisible =
      Boolean(this.topSearchesPanel && this.topSearchesPanel.style.display !== 'none');
    this.topSearchesPanel?.style.setProperty('display', 'none');
    if (!this.isOpen || topWasVisible) {
      this.toggleLoading(true);
    }
    this.getSearchResults(this.searchTerm);
  }

  getSearchResults(searchTerm) {
    const root = window.Shopify?.routes?.root ?? '/';
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-results',
      'resources[type]': 'query,product,collection,page,article',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
    });
    fetch(`${root}search/suggest?${params.toString()}`)
      .then(response => {
        if (!response.ok) {
          this.close();
          return Promise.reject(new Error(`Failed to fetch: ${response.status}`));
        }
        return response.text();
      })
      .then(text => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsMarkup = doc.querySelector('#shopify-section-predictive-results')?.innerHTML || '';
        this.updateResults(resultsMarkup);
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching search results:', error);
        this.close();
      });
  }

  updateResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.announce(this.predictiveSearchResults.querySelector('[data-predictive-count]')?.textContent.trim() ?? '');
    this.open();
  }

  /**
   * Writes to the live region so screen readers hear the result count.
   * @param {string} message - Localised text rendered by the predictive-results section
   */
  announce(message) {
    if (!this.statusRegion) return;
    this.statusRegion.textContent = message;
  }

  openTopSearches() {
    this.toggleLoading(false);
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
    }
    this.topSearchesPanel?.style.setProperty('display', 'block');
    this.dataset.state = 'top-searches';
    this.isOpen = true;
  }

  open() {
    this.toggleLoading(false);
    this.topSearchesPanel?.style.setProperty('display', 'none');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'block';
    }
    // Exposed so a host can react to results being on screen - the mobile drawer swaps its
    // navigation out for them. 'top-searches' is kept distinct because that panel is hidden
    // in the drawer, and the navigation must stay put when nothing is actually shown.
    this.dataset.state = 'results';
    this.isOpen = true;
  }

  close() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
    }
    this.topSearchesPanel?.style.setProperty('display', 'none');
    delete this.dataset.state;
    this.announce('');
    this.isOpen = false;
  }

  clearSearch(e) {
    e.preventDefault();
    if (this.input) {
      this.input.value = '';
    }
    this.onChange();
  }

  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  toggleLoading(show) {
    const loader = this.querySelector('.predictive-search__loading');
    if (loader) loader.style.display = show ? 'block' : 'none';
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
